import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { differenceInDays, format, parseISO, subDays } from 'date-fns';
import { useCanViewCost, useCostAccessPending, useMetricSeries } from '@/hooks/use-analytics-v2';
import type { Granularity, ScalarMetricKey, SeriesMetricKey } from '@/types/analyticsV2';
import { OverviewSection } from './overview-section';
import { TicketMetricsSection } from './ticket-metrics-section';
import { CategoriesSection } from './categories-section';
import { CoverageFunnelSection } from './coverage-funnel-section';
import { CostSection } from './cost-section';
import { MetricChart } from './metric-chart';
import { ChartMetricSelector } from './chart-metric-selector';
import { BrandScopePicker } from './brand-scope-picker';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { AccountPicker } from '@/modules/sentinel-analytics/account-picker';
import { metricDefs, isMetricSeriesAvailable } from '../metric-defs';
import { useAnalyticsSelection } from '../use-analytics-selection';

export type AccountAnalyticsTab = 'message' | 'ticket' | 'funnel' | 'cost';

const TITLES: Record<AccountAnalyticsTab, string> = {
  message: 'Message Metrics',
  ticket: 'Ticket Metrics',
  funnel: 'Coverage Funnel',
  cost: 'AI Cost',
};

// Default bucket size from the range (no granularity selector).
const pickGranularity = (fromDate: string, toDate: string): Granularity => {
  const days = differenceInDays(new Date(toDate), new Date(fromDate));
  if (days <= 31) return 'day';
  if (days <= 120) return 'week';
  return 'month';
};

// Label for the previous period the backend compares against (same length, ending the day before).
const comparisonLabel = (from: Date, to: Date): string => {
  const lengthDays = differenceInDays(to, from) + 1;
  const prevTo = subDays(from, 1);
  const prevFrom = subDays(prevTo, lengthDays - 1);
  return `${format(prevFrom, 'd MMM, yy')} – ${format(prevTo, 'd MMM, yy')}`;
};

function Notice({ title, detail }: { title: string; detail: string }) {
  return (
    <div className='flex min-h-48 flex-col items-center justify-center rounded-[22px] border border-gray-300 px-6 text-center'>
      <p className='font-medium text-black-300'>{title}</p>
      <p className='mt-1 text-sm text-gray-900'>{detail}</p>
    </div>
  );
}

/** One Internal Analytics page for one account, laid out like the Sentinel Analytics pages. */
export function SingleAccountAnalytics({ tab }: { tab: AccountAnalyticsTab }) {
  const [selection, setSelection] = useAnalyticsSelection();
  const [chartMetric, setChartMetric] = useState<ScalarMetricKey>('aiMessages');
  // AI Cost is only for emails on the cost allowlist; nothing cost-related requests until the backend says so.
  const canViewCost = useCanViewCost();
  const costAccessPending = useCostAccessPending();

  const { startDate: fromDate, endDate: toDate, buId } = selection;
  const accountId = selection.account ? Number(selection.account.id) : null;
  const startDate = useMemo(() => parseISO(fromDate), [fromDate]);
  const endDate = useMemo(() => parseISO(toDate), [toDate]);
  const granularity = useMemo(() => pickGranularity(fromDate, toDate), [fromDate, toDate]);
  const comparison = useMemo(() => comparisonLabel(startDate, endDate), [startDate, endDate]);

  const safeChartMetric: ScalarMetricKey = isMetricSeriesAvailable(chartMetric) ? chartMetric : 'aiMessages';
  const chartDef = metricDefs[safeChartMetric];
  const activeSeriesKey = chartDef.chart?.seriesKey;
  // Only Message Metrics draws the trend chart, and only once an account is picked (no keys, no request).
  const seriesKeys = useMemo<SeriesMetricKey[]>(
    () => (tab === 'message' && accountId && activeSeriesKey ? [activeSeriesKey] : []),
    [tab, accountId, activeSeriesKey],
  );
  const seriesQuery = useMetricSeries(seriesKeys, fromDate, toDate, granularity, accountId ?? 0, buId);
  const activeSeries = activeSeriesKey ? seriesQuery.data?.[activeSeriesKey] : undefined;

  let body;
  if (!accountId) body = <Notice title='Pick an account' detail='These pages show one account at a time.' />;
  else if (tab === 'message')
    body = (
      <div className='flex flex-col gap-6'>
        <OverviewSection fromDate={fromDate} toDate={toDate} comparisonLabel={comparison} accountId={accountId} buId={buId} />
        <div className='flex flex-col gap-3'>
          <h2 className='text-[12px] text-gray-800 font-medium'>Trends</h2>
          <MetricChart
            def={chartDef}
            series={activeSeries}
            isLoading={seriesQuery.isLoading}
            selector={<ChartMetricSelector value={safeChartMetric} onChange={setChartMetric} />}
          />
        </div>
      </div>
    );
  else if (tab === 'ticket')
    body = (
      <div className='flex flex-col gap-6'>
        <TicketMetricsSection fromDate={fromDate} toDate={toDate} comparisonLabel={comparison} accountId={accountId} buId={buId} />
        <CategoriesSection fromDate={fromDate} toDate={toDate} accountId={accountId} buId={buId} />
      </div>
    );
  else if (tab === 'funnel') body = <CoverageFunnelSection fromDate={fromDate} toDate={toDate} accountId={accountId} buId={buId} />;
  else if (canViewCost) body = <CostSection fromDate={fromDate} toDate={toDate} accountId={accountId} buId={buId} />;
  else if (!costAccessPending) body = <Notice title='No access to AI cost' detail='AI cost is limited to the emails on the cost allowlist.' />;

  return (
    <main className='h-full w-full overflow-y-auto px-4 pt-3.5 pb-8 font-inter'>
      <header className='mb-[18px] flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between'>
        <div className='flex items-center gap-1'>
          <Link
            to='/analytics'
            aria-label='Back to Overview'
            title='Back to Overview'
            className='-ml-1.5 flex size-7 shrink-0 items-center justify-center rounded-full text-gray-800 transition-colors hover:bg-gray-200 hover:text-ink'
          >
            <ChevronLeft aria-hidden='true' className='size-4' />
          </Link>
          <h1 className='text-base font-semibold leading-6 text-ink'>{TITLES[tab]}</h1>
        </div>
        <div aria-label='Analytics filters' className='flex flex-wrap items-center gap-2'>
          <AccountPicker value={selection.account} onChange={(account) => setSelection((s) => ({ ...s, account, buId: null }))} />
          {accountId ? <BrandScopePicker accountId={accountId} value={buId} onChange={(next) => setSelection((s) => ({ ...s, buId: next }))} /> : null}
          <DateRangePicker
            value={{ startDate, endDate }}
            onChange={(next) =>
              next.startDate && next.endDate && setSelection((s) => ({ ...s, startDate: format(next.startDate!, 'yyyy-MM-dd'), endDate: format(next.endDate!, 'yyyy-MM-dd') }))
            }
          />
        </div>
      </header>
      {body}
    </main>
  );
}
